import { useTimeStore } from '@store/common/useTimeStore.js'
import { getNextPollingTime } from '@utils/getNextPollingTime.js'

const groups = new Map()

const clearTimer = (group) => {
  if (group.timeoutId === null) return

  clearTimeout(group.timeoutId)
  group.timeoutId = null
}

let serverTimeOffset = null

const hasServerTimeOffset = () => serverTimeOffset !== null

const captureServerTimeOffset = () => {
  if (hasServerTimeOffset()) return

  const { serverTime, storedAt } = useTimeStore.getState()

  if (!serverTime) return

  serverTimeOffset = serverTime.getTime() - storedAt
}

const getServerNow = () => new Date(Date.now() + (serverTimeOffset ?? 0))

const isHidden = () => typeof document !== 'undefined' && document.hidden

// 동일 시각 태스크를 한 번에 실행
const runTasks = (group) => {
  Promise.allSettled([...group.tasks].map((task) => Promise.resolve().then(task)))
}

const schedule = (period) => {
  const group = groups.get(period)

  if (!group || group.tasks.size === 0) return

  clearTimer(group)

  captureServerTimeOffset()

  if (isHidden()) return

  const now = getServerNow()
  const next = getNextPollingTime(now, period)
  const delay = Math.max(next.getTime() - now.getTime(), 0)

  group.timeoutId = setTimeout(() => {
    group.timeoutId = null

    schedule(period)

    runTasks(group)
  }, delay)
}

let isPaused = false

const pauseAll = () => {
  if (isPaused) return

  isPaused = true

  groups.forEach((group) => clearTimer(group))
}

const resumeAll = () => {
  if (!isPaused) return

  if (isHidden()) return

  isPaused = false

  captureServerTimeOffset()

  // 다음 서버시각 경계로 타이머를 다시 정렬
  groups.forEach((_, period) => schedule(period))

  groups.forEach((group) => runTasks(group))
}

const handleVisibilityChange = () => {
  if (isHidden()) {
    pauseAll()
    return
  }

  resumeAll()
}

let unwatchVisibility = null

const stopWatchingVisibility = () => {
  if (!unwatchVisibility) return

  unwatchVisibility()
  unwatchVisibility = null
}

const watchVisibility = () => {
  if (unwatchVisibility || typeof document === 'undefined') return

  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('pageshow', handleVisibilityChange)
  window.addEventListener('pagehide', pauseAll)

  unwatchVisibility = () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('pageshow', handleVisibilityChange)
    window.removeEventListener('pagehide', pauseAll)
  }
}

let unwatchServerTime = null

const stopWatchingServerTime = () => {
  if (!unwatchServerTime) return

  unwatchServerTime()
  unwatchServerTime = null
}

const watchServerTimeOnce = () => {
  if (hasServerTimeOffset() || unwatchServerTime) return

  unwatchServerTime = useTimeStore.subscribe(() => {
    captureServerTimeOffset()

    if (!hasServerTimeOffset()) return

    stopWatchingServerTime()

    groups.forEach((_, period) => schedule(period))
  })
}

export const subscribeMStockPolling = (period, task) => {
  if (!period || typeof task !== 'function') return () => {}

  if (!groups.has(period)) {
    groups.set(period, { tasks: new Set(), timeoutId: null })
  }

  const group = groups.get(period)
  const isFirstTask = group.tasks.size === 0

  group.tasks.add(task)

  if (isFirstTask) {
    watchServerTimeOnce()
    watchVisibility()

    isPaused = isHidden()
    schedule(period)
  }

  return () => {
    group.tasks.delete(task)

    if (group.tasks.size > 0) return

    clearTimer(group)
    groups.delete(period)

    if (groups.size === 0) {
      stopWatchingServerTime()
      stopWatchingVisibility()
    }
  }
}
